import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  BarChart3,
  Users,
  Mail,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  History,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { signOut } from "firebase/auth";
import auth from "../../../../firebase.init";
import { useNavigate } from "react-router-dom";

const MailMarketingSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsMobileOpen(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSignout = () => {
    signOut(auth)
      .then(() => navigate("/login"))
      .catch((error) => console.error("Logout error:", error));
  };

  const navItems = [
    {
      path: "/admin/email-marketing",
      name: "Dashboard",
      icon: LayoutDashboard,
    },

    { path: "/admin/email-analytics", name: "Analytics", icon: BarChart3 },
    {
      path: "/admin/all-message-history",
      name: "All Message History",
      icon: History,
    },
    { path: "/admin/user-email-marketing", name: "Audience", icon: Users },
    { path: "/admin/email-templates", name: "Templates", icon: Mail },
    { path: "/admin/generate-email", name: "Generate Email", icon: Sparkles },
    { path: "/admin/user-marketing-smpt", name: "Settings", icon: Settings },
  ];

  const isActive = (path) => location.pathname === path;

  const sidebarContent = (
    <div
      className={`h-100 d-flex flex-column bg-dark-surface border-end border-dark-subtle transition-all`}
      style={{
        width: isCollapsed && !isMobile ? "80px" : "260px",
        zIndex: 1050,
      }}
    >
      {/* Header */}
      <div className="p-4 d-flex align-items-center justify-content-between border-bottom border-dark-subtle">
        {(!isCollapsed || isMobile) && (
          <div className="d-flex align-items-center gap-2 overflow-hidden">
            <div className="bg-primary rounded p-1 d-flex align-items-center justify-content-center">
              <Mail size={20} className="text-white" />
            </div>
            <span className="text-white fw-bold fs-6 text-truncate">
              Mail Studio
            </span>
          </div>
        )}
        {isCollapsed && !isMobile && (
          <div className="w-100 d-flex justify-content-center">
            <div className="bg-primary rounded p-1 d-flex align-items-center justify-content-center">
              <Mail size={24} className="text-white" />
            </div>
          </div>
        )}

        {isMobile && (
          <button
            className="btn btn-link text-white p-0"
            onClick={() => setIsMobileOpen(false)}
          >
            <X size={24} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-grow-1 overflow-y-auto py-4 px-3 custom-scrollbar">
        <div className="d-flex flex-column gap-2">
          {(!isCollapsed || isMobile) && (
            <span
              className="text-secondary fw-semibold text-uppercase"
              style={{
                fontSize: "11px",
                letterSpacing: "0.5px",
                paddingLeft: "12px",
                marginBottom: "8px",
              }}
            >
              Menu
            </span>
          )}
          {navItems.map((item) => {
            const active = isActive(item.path);
            const Icon = item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`d-flex align-items-center gap-3 px-3 py-2 rounded-3 text-decoration-none transition-all ${active ? "bg-primary-soft text-primary shadow-sm" : "text-secondary hover-bg-dark"}`}
                title={isCollapsed && !isMobile ? item.name : ""}
              >
                <Icon
                  size={20}
                  className={active ? "text-primary" : ""}
                  strokeWidth={active ? 2.5 : 2}
                />
                {(!isCollapsed || isMobile) && (
                  <span
                    className={`fw-medium ${active ? "text-white" : ""}`}
                    style={{ fontSize: "14px" }}
                  >
                    {item.name}
                  </span>
                )}
                {active && (!isCollapsed || isMobile) && (
                  <motion.div
                    layoutId="activeNav"
                    className="ms-auto w-1 h-50 bg-primary rounded-pill"
                    style={{ width: "4px" }}
                  />
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Footer / User Actions */}
      <div className="p-3 border-top border-dark-subtle mt-auto">
        <button
          onClick={handleSignout}
          className={`btn btn-link text-decoration-none w-100 d-flex align-items-center ${isCollapsed && !isMobile ? "justify-content-center px-0" : "px-3"} py-2 rounded-3 text-secondary hover-bg-dark transition-all gap-3`}
          title={isCollapsed && !isMobile ? "Logout" : ""}
        >
          <LogOut size={20} />
          {(!isCollapsed || isMobile) && (
            <span className="fw-medium text-white" style={{ fontSize: "14px" }}>
              Logout
            </span>
          )}
        </button>
      </div>

      {/* Collapse Toggle (Desktop only) */}
      {!isMobile && (
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="position-absolute bg-dark-surface border border-dark-subtle rounded-circle d-flex align-items-center justify-content-center text-secondary hover-text-white transition-all shadow"
          style={{
            width: "28px",
            height: "28px",
            top: "28px",
            right: "-14px",
            zIndex: 1060,
          }}
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      )}

      <style jsx="true">{`
        .bg-dark-surface {
          background-color: #0f172a;
        }
        .border-dark-subtle {
          border-color: rgba(255, 255, 255, 0.08) !important;
        }
        .bg-primary-soft {
          background-color: rgba(13, 110, 253, 0.15);
        }
        .hover-bg-dark:hover {
          background-color: rgba(255, 255, 255, 0.05);
          color: #fff !important;
        }
        .hover-text-white:hover {
          color: #fff !important;
        }
        .transition-all {
          transition: all 0.2s ease-in-out;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
        }
      `}</style>
    </div>
  );

  return (
    <>
      {/* Mobile Toggle Button */}
      {isMobile && (
        <div
          className="d-md-none p-3 border-bottom border-dark-subtle position-sticky top-0 w-100 z-3"
          style={{ backgroundColor: "#0f172a" }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <div className="bg-primary rounded p-1 d-flex align-items-center justify-content-center">
                <Mail size={18} className="text-white" />
              </div>
              <span className="text-white fw-bold">Mail Studio</span>
            </div>
            <button
              className="btn btn-link text-white p-0"
              onClick={() => setIsMobileOpen(true)}
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <div
          className="position-sticky top-0 h-100 flex-shrink-0"
          style={{ height: "100vh" }}
        >
          {sidebarContent}
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isMobile && isMobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-50"
              style={{ zIndex: 1040 }}
              onClick={() => setIsMobileOpen(false)}
            />
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="position-fixed top-0 start-0 h-100"
              style={{ zIndex: 1050 }}
            >
              {sidebarContent}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default MailMarketingSidebar;
